/* ── Cart Drawer Custom Element ── */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel             = this.querySelector('.cart-drawer__panel');
    this.itemsContainer    = this.querySelector('[data-cart-items]');
    this.emptyState        = this.querySelector('[data-cart-empty]');
    this.footer            = this.querySelector('[data-cart-footer]');
    this.subtotalEl        = this.querySelector('[data-cart-subtotal]');
    this.incentiveText     = this.querySelector('[data-cart-incentive-text]');
    this.incentiveProgress = this.querySelector('[data-cart-incentive-progress]');
    this.upsellZone        = this.querySelector('[data-cart-upsell]');
    this.upsellItemsEl     = this.querySelector('[data-cart-upsell-items]');
    this.discountZone      = this.querySelector('[data-cart-discount]');
    this.discountForm      = this.querySelector('[data-discount-form]');
    this.discountInput     = this.querySelector('[data-discount-input]');
    this.discountMsg       = this.querySelector('[data-discount-msg]');
    this.discountTags      = this.querySelector('[data-discount-tags]');
    this.threshold         = parseInt(document.body.dataset.freeShippingThreshold || '0', 10);

    this._lastUpsellProductId = null;
    this._appliedDiscounts    = [];

    this.querySelectorAll('[data-cart-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', this.close.bind(this));
    }.bind(this));

    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.discountForm) {
      this.discountForm.addEventListener('submit', this.onDiscountSubmit.bind(this));
    }

    this.unsubscribeOpen    = window.pubsub.subscribe('cart:open',    this.open.bind(this));
    this.unsubscribeUpdated = window.pubsub.subscribe('cart:updated', this.refresh.bind(this));

    /* Délégation d'événements — bindée UNE SEULE FOIS ici, jamais sur les items */
    this._bindCartActions();

    /* Restaurer l'état ouvert entre les pages */
    try {
      if (localStorage.getItem('stratos-cart-open') === '1') {
        this.open();
      }
    } catch (_) {}
  }

  disconnectedCallback() {
    if (this.unsubscribeOpen)    this.unsubscribeOpen();
    if (this.unsubscribeUpdated) this.unsubscribeUpdated();
  }

  /* ── Open / close ── */

  open() {
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this._triggerEl = document.activeElement;
    if (this.panel) this.panel.focus();
    this.loadCart();
    try { localStorage.setItem('stratos-cart-open', '1'); } catch (_) {}
  }

  close() {
    this.setAttribute('hidden', '');
    this.removeAttribute('open');
    document.body.style.overflow = '';
    try { localStorage.removeItem('stratos-cart-open'); } catch (_) {}
    if (this._triggerEl && typeof this._triggerEl.focus === 'function') {
      this._triggerEl.focus();
    }
  }

  onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this._trapFocus(e);
    }
  }

  _trapFocus(e) {
    var focusable = Array.from(this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return !el.closest('[hidden]'); });

    if (!focusable.length) { e.preventDefault(); return; }

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || document.activeElement === this.panel) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Cart data ── */

  loadCart() {
    /* Skeleton pendant le fetch */
    if (this.itemsContainer && !this.itemsContainer.hasChildNodes()) {
      this.itemsContainer.innerHTML = this._skeletonHTML(3);
    }
    var self = this;
    window.ShopUtils.fetchCart()
      .then(this.renderCart.bind(this))
      .catch(function () {
        // Fetch échoué : effacer le skeleton et afficher l'état vide
        if (self.itemsContainer) self.itemsContainer.innerHTML = '';
        if (self.emptyState)     self.emptyState.removeAttribute('hidden');
        if (self.footer)         self.footer.setAttribute('hidden', '');
      });
  }

  _skeletonHTML(count) {
    var item = '<div class="cart-item cart-item--skeleton">'
      + '<span class="skeleton" style="width:80px;height:107px;flex-shrink:0;border-radius:var(--r-sm,4px)"></span>'
      + '<div style="flex:1;display:flex;flex-direction:column;gap:8px;padding:4px 0">'
      + '<span class="skeleton skeleton--text" style="width:70%"></span>'
      + '<span class="skeleton skeleton--text" style="width:40%"></span>'
      + '<span class="skeleton skeleton--price"></span>'
      + '</div>'
      + '</div>';
    var html = '';
    for (var i = 0; i < count; i++) html += item;
    return html;
  }

  refresh() {
    this.loadCart();
  }

  renderCart(cart) {
    if (!cart || !this.itemsContainer) return;

    var isEmpty = cart.item_count === 0;

    if (isEmpty) {
      this.itemsContainer.innerHTML = '';
      if (this.emptyState)   this.emptyState.removeAttribute('hidden');
      if (this.footer)       this.footer.setAttribute('hidden', '');
      if (this.upsellZone)   this.upsellZone.setAttribute('hidden', '');
      if (this.discountZone) this.discountZone.setAttribute('hidden', '');
    } else {
      if (this.emptyState)   this.emptyState.setAttribute('hidden', '');
      if (this.footer)       this.footer.removeAttribute('hidden');
      if (this.discountZone) this.discountZone.removeAttribute('hidden');

      this.renderItems(cart.items);

      if (this.subtotalEl) {
        this.subtotalEl.textContent = window.ShopUtils.formatMoney(cart.total_price);
      }

      this.renderAppliedDiscounts(cart.cart_level_discount_applications || []);
      this.loadUpsell(cart.items);
    }

    this.updateIncentive(cart.total_price);
    /* NE PAS appeler bindItemActions() ici — la délégation dans connectedCallback suffit */
  }

  /* ── Items ── */

  /* Build price HTML for an item */
  _itemPriceHTML(item) {
    var html = '';
    if (item.original_price !== item.final_price) {
      html += '<span class="cart-item__price-compare">' + window.ShopUtils.formatMoney(item.original_price) + '</span>';
    }
    html += '<span class="cart-item__price-final">' + window.ShopUtils.formatMoney(item.final_price) + '</span>';
    return html;
  }

  /* Build full inner HTML for an item element */
  _itemInnerHTML(item) {
    var imageHtml = item.image
      ? '<img'
        + ' src="' + this.getSizedImageUrl(item.image, '160x214') + '"'
        + ' srcset="'
          + this.getSizedImageUrl(item.image, '80x107') + ' 80w, '
          + this.getSizedImageUrl(item.image, '160x214') + ' 160w, '
          + this.getSizedImageUrl(item.image, '320x428') + ' 320w"'
        + ' sizes="80px" width="80" height="107"'
        + ' alt="' + this.escapeHtml(item.title) + '"'
        + ' loading="lazy" style="object-fit:cover;width:100%;height:100%;display:block;">'
      : '';
    var variantHtml = item.variant_title
      ? '<p class="cart-item__variant">' + this.escapeHtml(item.variant_title) + '</p>'
      : '';
    return '<div class="cart-item__media">' + imageHtml + '</div>'
      + '<div class="cart-item__info">'
      + '<a href="' + item.url + '" class="cart-item__title">' + this.escapeHtml(item.product_title) + '</a>'
      + variantHtml
      + '<div class="cart-item__price">' + this._itemPriceHTML(item) + '</div>'
      + '<div class="cart-item__actions">'
      + '<quantity-input class="quantity-input" data-key="' + item.key + '">'
      + '<button type="button" class="quantity-input__btn quantity-input__btn--minus" name="minus" aria-label="Diminuer"' + (item.quantity <= 0 ? ' disabled' : '') + '>'
      + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>'
      + '</button>'
      + '<input type="number" class="quantity-input__field" value="' + item.quantity + '" min="0" aria-label="Quantité" inputmode="numeric">'
      + '<button type="button" class="quantity-input__btn quantity-input__btn--plus" name="plus" aria-label="Augmenter">'
      + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
      + '</button>'
      + '</quantity-input>'
      + '<button type="button" class="cart-item__remove button button--icon" data-cart-remove="' + item.key + '" aria-label="Supprimer">'
      + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>'
      + '</button>'
      + '</div>'
      + '</div>';
  }

  /* Diff-based render — only mutates what changed */
  renderItems(items) {
    var self     = this;
    var container = this.itemsContainer;
    if (!container) return;

    /* Purge skeleton items left over from loadCart() — ils n'ont pas data-key
       donc le diff ci-dessous ne les trouve jamais et ils resteraient indéfiniment */
    container.querySelectorAll('.cart-item--skeleton').forEach(function (el) {
      el.parentNode.removeChild(el);
    });

    /* Index existing DOM nodes by item key */
    var existingEls = {};
    container.querySelectorAll('[data-cart-item][data-key]').forEach(function (el) {
      existingEls[el.dataset.key] = el;
    });

    /* Keys that should remain after this render */
    var newKeys = {};
    items.forEach(function (item) { newKeys[item.key] = true; });

    /* 1. Remove items no longer in cart (animate-out → remove) */
    Object.keys(existingEls).forEach(function (key) {
      if (!newKeys[key]) {
        var el = existingEls[key];
        el.classList.add('cart-item--removing');
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 280);
      }
    });

    /* 2. Update existing / insert new — preserve DOM order */
    items.forEach(function (item, idx) {
      var el = existingEls[item.key];

      if (el) {
        /* ── Patch in-place ── */
        /* Ré-activer tous les contrôles (peuvent être disabled depuis _safeChangeCart) */
        var input = el.querySelector('.quantity-input__field');
        var minus = el.querySelector('[name="minus"]');
        var plus  = el.querySelector('[name="plus"]');
        var rm    = el.querySelector('[data-cart-remove]');
        if (input) { input.disabled = false; if (parseInt(input.value, 10) !== item.quantity) input.value = item.quantity; }
        if (minus) minus.disabled = (item.quantity <= 1);
        if (plus)  plus.disabled  = false;
        if (rm)    rm.disabled    = false;
        /* Price */
        var priceEl = el.querySelector('.cart-item__price');
        var newPriceHTML = self._itemPriceHTML(item);
        if (priceEl && priceEl.innerHTML !== newPriceHTML) {
          priceEl.innerHTML = newPriceHTML;
          priceEl.classList.add('cart-item__price--updated');
          setTimeout(function () { priceEl.classList.remove('cart-item__price--updated'); }, 600);
        }
        /* Ensure correct DOM position */
        var children = container.querySelectorAll('[data-cart-item]');
        if (children[idx] !== el) container.insertBefore(el, children[idx] || null);
      } else {
        /* ── Create & insert ── */
        var newEl = document.createElement('div');
        newEl.className = 'cart-item cart-item--entering';
        newEl.setAttribute('data-cart-item', '');
        newEl.setAttribute('data-key', item.key);
        newEl.innerHTML = self._itemInnerHTML(item);
        container.appendChild(newEl);
        /* Trigger enter animation next frame */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { newEl.classList.remove('cart-item--entering'); });
        });
      }
    });
  }

  /* ── Event delegation (bindée une fois dans connectedCallback) ── */

  _bindCartActions() {
    var self = this;
    this._cartUpdating = false;

    if (!this.itemsContainer) return;

    /* <quantity-input> dispatch 'quantity:change' avec e.detail.value déjà correct
       → pas de lecture de input.value ici, zéro risque de double incrément */
    this.itemsContainer.addEventListener('quantity:change', function (e) {
      if (self._cartUpdating) return;
      var qi = e.target;
      if (!qi || !qi.dataset || !qi.dataset.key) return;
      self._safeChangeCart(qi.dataset.key, e.detail.value, qi);
    });

    /* Suppression d'article (pas géré par quantity-input) */
    this.itemsContainer.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn && !removeBtn.disabled && !self._cartUpdating) {
        var key = removeBtn.getAttribute('data-cart-remove');
        self._safeChangeCart(key, 0, removeBtn.closest('[data-cart-item]') || removeBtn);
      }
    });
  }

  /* Exécute changeCart avec flag anti-boucle + lock visuel */
  _safeChangeCart(key, qty, container) {
    if (this._cartUpdating) return;
    var self = this;
    this._cartUpdating = true;

    /* Désactiver les contrôles visuellement */
    if (container) {
      container.querySelectorAll('button, input').forEach(function (el) { el.disabled = true; });
    }

    window.ShopUtils.changeCart(key, qty)
      .finally(function () {
        self._cartUpdating = false;
        /* Les contrôles sont ré-activés dans renderItems au prochain render */
      });
  }

  /* Conservée vide pour compatibilité */
  bindItemActions() {}

  /* ── Incentive bar — double palier relais + domicile ── */

  updateIncentive(totalPrice) {
    var homeThreshold  = this.threshold;          /* data-free-shipping-threshold sur body */
    var relayThreshold = parseInt(document.body.dataset.freeShippingRelay || '0', 10);

    /* Double palier uniquement si relay < home (tous les deux > 0) */
    var hasRelay = relayThreshold > 0 && homeThreshold > relayThreshold;

    /* Si relay est défini mais home est mal configuré (home <= relay),
       on utilise relay comme seuil unique pour ne pas bloquer */
    var maxCents;
    if (hasRelay) {
      maxCents = homeThreshold * 100;
    } else if (relayThreshold > 0) {
      maxCents = relayThreshold * 100;   /* relay seul, home non configuré */
    } else {
      maxCents = homeThreshold * 100;    /* single-tier classique */
    }
    var relayCents = relayThreshold * 100;

    if (!maxCents || !this.incentiveProgress) return;

    /* Barre de progression (0 → 100% = seuil max) */
    var progress = Math.min(100, (totalPrice / maxCents) * 100);
    this.incentiveProgress.style.width = progress + '%';

    /* Tick du palier relais */
    var tick = this.querySelector('[data-cart-incentive-tick]');
    if (tick && hasRelay) {
      var relayPct = Math.round((relayCents / maxCents) * 100);
      tick.style.left = relayPct + '%';
      tick.classList.toggle('is-achieved', totalPrice >= relayCents);
    }

    /* Texte */
    if (!this.incentiveText) return;
    var t = this.incentiveText;
    var fmt = function (cents) {
      return window.ShopUtils && window.ShopUtils.formatMoney
        ? window.ShopUtils.formatMoney(cents)
        : (cents / 100).toFixed(2).replace('.', ',') + '\u00a0€';
    };

    if (totalPrice >= maxCents) {
      /* Tout atteint */
      t.textContent = t.dataset.msgAllOk || 'Livraison offerte débloquée !';
      t.dataset.achievedState = 'all';
    } else if (hasRelay && totalPrice >= relayCents) {
      /* Relais atteint, domicile pas encore */
      var remainHome = fmt(maxCents - totalPrice);
      var msgRelayOk = (t.dataset.msgRelayOk || '✓ Point relais offert · Plus que __AMOUNT__ pour la livraison à domicile')
        .replace('__AMOUNT__', remainHome);
      t.textContent = msgRelayOk;
      t.dataset.achievedState = 'relay';
    } else {
      /* Rien atteint — viser le prochain palier */
      var targetCents = hasRelay ? relayCents : maxCents;
      var remaining   = fmt(targetCents - totalPrice);
      var tpl = hasRelay
        ? (t.dataset.tplRelay || 'Plus que __AMOUNT__ pour la livraison offerte en point relais')
        : (relayThreshold > 0
            ? (t.dataset.tplRelay || 'Plus que __AMOUNT__ pour la livraison offerte en point relais')
            : (t.dataset.tplHome  || 'Plus que __AMOUNT__ pour la livraison offerte'));
      t.textContent = tpl.replace('__AMOUNT__', remaining);
      t.dataset.achievedState = 'none';
    }
  }

  /* ── Upsell (Shopify Recommendations API) ── */

  loadUpsell(items) {
    if (!items || !items.length || !this.upsellZone || !this.upsellItemsEl) return;

    var topProduct = items[0];
    if (!topProduct || !topProduct.product_id) return;
    var productId = topProduct.product_id;

    if (this._lastUpsellProductId === productId) return;
    this._lastUpsellProductId = productId;

    var cartProductIds = items.map(function (i) { return i.product_id; });
    var self = this;

    fetch('/recommendations/products.json?product_id=' + productId + '&limit=4&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.products || !data.products.length) {
          if (self.upsellZone) self.upsellZone.setAttribute('hidden', '');
          return;
        }

        var filtered = data.products.filter(function (p) {
          return cartProductIds.indexOf(p.id) === -1;
        });

        if (!filtered.length) {
          if (self.upsellZone) self.upsellZone.setAttribute('hidden', '');
          return;
        }

        var html = '';
        filtered.forEach(function (p) {
          var price      = p.price_min ? window.ShopUtils.formatMoney(p.price_min) : '';
          var imgBase    = p.featured_image || '';
          var imgSrc     = imgBase ? imgBase.replace(/\.([^.?]+)(\?.*)?$/, '_112x150.$1$2') : '';
          var imgSrc2x   = imgBase ? imgBase.replace(/\.([^.?]+)(\?.*)?$/, '_224x300.$1$2') : '';
          var hasVariants = p.variants && p.variants.length > 1;
          var variantId  = p.variants && p.variants[0] ? p.variants[0].id : '';

          /* Produit à 1 variante → ajout direct ; à N variantes → modal quick-add */
          var lChoose = (window.StratOSLocales && window.StratOSLocales.cart.upsell_choose) || 'Choisir';
          var lAdd    = (window.StratOSLocales && window.StratOSLocales.cart.upsell_add)    || '+ Ajouter';
          var addBtn = hasVariants
            ? '<button type="button" class="cart-drawer__upsell-add" data-quick-add-modal="' + p.handle + '" aria-label="' + lChoose + ' ' + self.escapeHtml(p.title) + '">' + lChoose + '</button>'
            : '<button type="button" class="cart-drawer__upsell-add" data-upsell-add="' + variantId + '" aria-label="' + lAdd + ' ' + self.escapeHtml(p.title) + '">' + lAdd + '</button>';

          html += '<div class="cart-drawer__upsell-card">'
            + (imgSrc ? '<img src="' + imgSrc + '" srcset="' + imgSrc + ' 1x, ' + imgSrc2x + ' 2x" alt="' + self.escapeHtml(p.title) + '" width="56" height="75" loading="lazy" class="cart-drawer__upsell-image">' : '')
            + '<div class="cart-drawer__upsell-info">'
            + '<p class="cart-drawer__upsell-name">' + self.escapeHtml(p.title) + '</p>'
            + '<p class="cart-drawer__upsell-price">' + price + '</p>'
            + addBtn
            + '</div>'
            + '</div>';
        });

        self.upsellItemsEl.innerHTML = html;
        self.upsellZone.removeAttribute('hidden');
        self.bindUpsellActions();
      })
      .catch(function () {
        if (self.upsellZone) self.upsellZone.setAttribute('hidden', '');
      });
  }

  bindUpsellActions() {
    this.querySelectorAll('[data-upsell-add]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = parseInt(btn.dataset.upsellAdd, 10);
        if (!variantId) return;

        btn.classList.add('is-loading');
        btn.disabled = true;

        window.ShopUtils.addToCart(variantId, 1)
          .then(function () {
            btn.textContent = '✓';
            btn.disabled = true;
          })
          .catch(function () {
            btn.classList.remove('is-loading');
            btn.disabled = false;
          });
      });
    });
  }

  /* ── Discount code ── */

  onDiscountSubmit(e) {
    e.preventDefault();
    if (!this.discountInput) return;

    var code = this.discountInput.value.trim();
    if (!code) return;

    this.setDiscountMsg('', '');

    fetch('/discount/' + encodeURIComponent(code) + '?return_to=/', {
      method: 'GET',
      redirect: 'manual'
    })
      .then(function (r) {
        if (r.type === 'opaqueredirect' || r.ok) {
          return { ok: true };
        }
        return { ok: false };
      })
      .then(function (result) {
        if (result.ok) {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        }
        throw new Error('invalid');
      })
      .then(function (cart) {
        var discounts = cart.cart_level_discount_applications || [];
        var found     = discounts.some(function (d) { return d.title && d.title.toUpperCase() === code.toUpperCase(); });

        var lApplied = (window.StratOSLocales && window.StratOSLocales.cart.discount_applied) || 'Code appliqué !';
        var lInvalid = (window.StratOSLocales && window.StratOSLocales.cart.discount_invalid) || 'Code invalide ou expiré.';
        if (found || discounts.length > 0) {
          this.setDiscountMsg(lApplied, 'success');
          if (this.discountInput) this.discountInput.value = '';
          this.renderAppliedDiscounts(discounts);
          this.renderCart(cart);
        } else {
          this.setDiscountMsg(lInvalid, 'error');
        }
      }.bind(this))
      .catch(function () {
        var lInvalid = (window.StratOSLocales && window.StratOSLocales.cart.discount_invalid) || 'Code invalide ou expiré.';
        this.setDiscountMsg(lInvalid, 'error');
      }.bind(this));
  }

  setDiscountMsg(text, type) {
    if (!this.discountMsg) return;
    if (!text) {
      this.discountMsg.setAttribute('hidden', '');
      this.discountMsg.textContent = '';
      return;
    }
    this.discountMsg.textContent = text;
    this.discountMsg.className   = 'cart-drawer__discount-msg cart-drawer__discount-msg--' + type;
    this.discountMsg.removeAttribute('hidden');
  }

  renderAppliedDiscounts(discounts) {
    if (!this.discountTags) return;
    if (!discounts || !discounts.length) {
      this.discountTags.innerHTML = '';
      return;
    }

    var html = '';
    discounts.forEach(function (d) {
      html += '<span class="cart-drawer__discount-tag">'
        + '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>'
        + d.title
        + ' &minus;' + (d.value_type === 'percentage' ? parseFloat(d.value) + '%' : window.ShopUtils.formatMoney(d.total_allocated_amount))
        + '</span>';
    });
    this.discountTags.innerHTML = html;
  }

  /* ── Helpers ── */

  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url.replace(/\.([^.]+)$/, '_' + size + '.$1');
  }

  escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('cart-drawer', CartDrawer);
